import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react';
import { MermaidDiagram } from './MermaidDiagram';

type CodeElementProps = ComponentPropsWithoutRef<'code'> & {
  className?: string;
  children?: ReactNode;
};

type PreElementProps = ComponentPropsWithoutRef<'pre'> & {
  children?: ReactNode;
};


function extractMermaidSource(children: ReactNode): string | null {
  if (!children) return null;

  const codeElement = children as ReactElement<CodeElementProps>;
  if (!codeElement.props || typeof codeElement.props.className !== 'string') return null;
  if (!codeElement.props.className.includes('language-mermaid')) return null;

  const codeChildren = codeElement.props.children;
  if (typeof codeChildren === 'string') return codeChildren.trim();
  if (Array.isArray(codeChildren)) return codeChildren.join('').trim();

  return null;
}

// Explicit prop types for each element avoid the problematic MDXComponents
// generic from mdx/types (which isn't a standalone dependency).
export const mdxComponents = {
  h1: (props: ComponentPropsWithoutRef<'h1'>) => <h1 className="prose__h1" {...props} />,
  h2: (props: ComponentPropsWithoutRef<'h2'>) => <h2 className="prose__h2" {...props} />,
  h3: (props: ComponentPropsWithoutRef<'h3'>) => <h3 className="prose__h3" {...props} />,
  p: ({ children, ...rest }: ComponentPropsWithoutRef<'p'>) => {
    const firstChild = Array.isArray(children) ? children[0] : children;
    if (typeof firstChild === 'string') {
      const match = firstChild.match(/^:::(center|right|justify) ?(.*)/s);
      if (match) {
        const [, alignment, remainder] = match;
        const restChildren = Array.isArray(children) ? children.slice(1) : [];
        return (
          <p className="prose__p" style={{ textAlign: alignment as 'center' | 'right' | 'justify' }} {...rest}>
            {remainder || null}
            {restChildren}
          </p>
        );
      }
    }
    return <p className="prose__p" {...rest}>{children}</p>;
  },
  ul: (props: ComponentPropsWithoutRef<'ul'>) => <ul className="prose__ul" {...props} />,
  ol: (props: ComponentPropsWithoutRef<'ol'>) => <ol className="prose__ol" {...props} />,
  li: (props: ComponentPropsWithoutRef<'li'>) => <li className="prose__li" {...props} />,
  a: (props: ComponentPropsWithoutRef<'a'>) => <a className="prose__a" {...props} />,
  blockquote: (props: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="prose__blockquote" {...props} />
  ),
  table: (props: ComponentPropsWithoutRef<'table'>) => <table className="prose__table" {...props} />,
  thead: (props: ComponentPropsWithoutRef<'thead'>) => <thead className="prose__thead" {...props} />,
  tbody: (props: ComponentPropsWithoutRef<'tbody'>) => <tbody className="prose__tbody" {...props} />,
  tr: (props: ComponentPropsWithoutRef<'tr'>) => <tr className="prose__tr" {...props} />,
  th: (props: ComponentPropsWithoutRef<'th'>) => <th className="prose__th" {...props} />,
  td: (props: ComponentPropsWithoutRef<'td'>) => <td className="prose__td" {...props} />,
  code: (props: ComponentPropsWithoutRef<'code'>) => <code className="prose__code" {...props} />,
  pre: ({ children, ...props }: PreElementProps) => {
    const mermaidSource = extractMermaidSource(children);
    if (mermaidSource) return <MermaidDiagram chart={mermaidSource} />;

    return <pre className="prose__pre" {...props}>{children}</pre>;
  },
};
